import { themeColors } from '@/theme/colors';
import { IonButton, IonCol, IonIcon, IonRow, IonText } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';

import { Icon } from '@/components/Icon';
import styles from '@/components/Toasts/InfoToast.module.css';

import { mergeClassNames } from '@/utils/mergeClassNames';

type legendToastProps = {
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
  message: string;
};

/**
 * LegendToast component displays a toast notification with legend entries.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isOpen - Determines if the toast is open.
 * @param {function} props.onClose - Function to call when the toast closes.
 * @param {number} [props.duration=5000] - Duration in milliseconds before the toast automatically closes.
 *
 * @returns {JSX.Element | null} The rendered LegendToast component or null if not open.
 */
export const InfoToast = ({ isOpen, onClose, duration = 3000, message }: legendToastProps) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(onClose, 500);
      }, duration);

      // Clear the timer if the component unmounts
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose, duration]);

  if (!isOpen && !isClosing) return null;

  return (
    isOpen && (
      <div className={mergeClassNames(styles.infoToast, isClosing ? styles['slide-out'] : undefined)}>
        <div className={styles.closeButton}>
          <IonButton onClick={onClose} shape='round' size='small'>
            <IonIcon icon={closeOutline} size='small' slot='icon-only' />
          </IonButton>
        </div>

        <div className={styles.infoToast_Container}>
          <IonRow className='ion-align-items-center'>
            <IonCol size='auto'>
              <Icon color={themeColors.succesColor} name='info' />
            </IonCol>
            <IonCol>
              <IonText className={styles.title}>Tip</IonText>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonText className={styles.message}>{message}</IonText>
            </IonCol>
          </IonRow>
        </div>
      </div>
    )
  );
};
